import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { requestResponse } from '../Requests';
import { startCase } from 'lodash';
import { adminRejectedChangeRequest } from '../../../../utils/enums';
import { pathname, faqUrl } from '../../../../utils';
import { useHistory } from 'react-router';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import themeInfo from '../../../../themes/themes';
import LoadingButton from '../../../lowLevel/LoadingButton';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import UrlLink from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import AdminFreeText from './AdminFreeText';

const useStyles = makeStyles((theme: any) => ({
  logo: {
    width: 70,
    marginBottom: '10px',
    marginTop: '40px',
  },
  mailLogo: {
    fontSize: '70px',
  },
  topMargin: {
    marginTop: theme.spacing(2),
    fontWeight: 'bold',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  dialogAction: { display: 'block' },
  button: {
    marginTop: theme.spacing(2),
    minWidth: 175,
  },
  message: {
    marginRight: 'auto',
  },
}));

function showRejectedLineMessage(
  rejectReason: number,
  incompleteData: () => Object,
  mistake: string,
  unjustifiedRequest: () => Object,
  otherMessage: () => Object,
  lineNames: Array<string> = [''],
  classes: any,
  firstName: Object,
) {
  const theme = process.env.REACT_APP_THEME;

  const startingMessage = (): Object => (
    <>
      <Typography align="left" className={classes.message}>
        {firstName}
      </Typography>
      <Typography align="left" className={classes.message}>
        Our team received your recent request to change the following Line
        {lineNames.length > 1 ? 's' : ''}:
      </Typography>
      {lineNames?.map((name, id) => {
        return (
          <Typography
            align="left"
            key={id}
            className={classes.message}
            gutterBottom={id === lineNames.length - 1 ? true : false}
          >
            <li>{name} </li>
          </Typography>
        );
      })}{' '}
    </>
  );

  return (
    <>
      {startingMessage()}

      {rejectReason ===
        adminRejectedChangeRequest.UnsupportedGrowthAttempting && (
        <>
          <Typography paragraph align="left">
            We are unable to approve this request at this time, over concerns
            about the expected growth rate, given that the number of units you
            plan to purchase exceeds sales projections.
          </Typography>
          <Typography paragraph align="left">
            This may be due to a mistake during data-entry (e.g. inputting
            10,000 units instead of 1,000), or {themeInfo[theme].company}{' '}
            missing information supporting this rate of growth.
          </Typography>
          <Typography paragraph align="left">
            If you would still like to pursue this change, we encourage you to
            resubmit your request after having re-examined the unit count, and
            include any supporting information that can help our team make a
            decision.
          </Typography>
        </>
      )}
      {rejectReason === adminRejectedChangeRequest.IncompleteData && (
        <>{incompleteData()}</>
      )}
      {rejectReason === adminRejectedChangeRequest.FastExposureChanges && (
        <>
          <Typography paragraph align="left">
            We are unable to approve this request until the following
            requirements are met:
            <li>
              {' '}
              All Lines for a given product should start and end in succession,
              the marker of a periodical supply chain.{' '}
            </li>
            <li>
              {' '}
              Highly recommended: Avoid going out of stock by building Lines for
              a given product with partial overlap. One Line should start before
              the previous has ended, to avoid going out of stock.{' '}
            </li>
          </Typography>
          <Typography paragraph align="left">
            We have returned your Lines to ‘Draft’ to give you an opportunity to
            revise and resubmit.
          </Typography>
          <Typography paragraph align="left">
            If you believe we made a mistake, please resubmit your request and
            include any supporting information that can help our team make a
            decision.
          </Typography>
        </>
      )}
      {rejectReason === adminRejectedChangeRequest.LowMargin && (
        <>
          <Typography paragraph align="left">
            We are unable to approve this request due to concerns over
            profitability.
          </Typography>
          <Typography paragraph align="left">
            This can happen if you have seen increases in COGS and have updated
            your product’s retail price as a result, but did not update it on
            your {themeInfo[theme].company} account. If this is the case, please
            edit the MSRP field of your product directly from Lines, and
            resubmit your request. Another common reason is a mistake during
            submission, e.g. accidentally entering a $75,000 marketing cost
            instead of $7,500.
          </Typography>
          <Typography paragraph align="left">
            {mistake}
          </Typography>
        </>
      )}
      {rejectReason === adminRejectedChangeRequest.WrongVehicle && (
        <>
          <Typography paragraph align="left">
            We are unable to approve this request since it appears it should
            have been submitted via <b>Funds Deliveries </b> instead.
          </Typography>
          <Typography paragraph align="left">
            Please ensure to only submit change requests via Lines if a material
            change has occurred in your supply chain, such as a freight delay,
            the cancellation of a PO, or a change in the cost of a stage.
          </Typography>
          <Typography paragraph align="left">
            If you are experiencing or anticipating a cash flow surplus or
            shortage and would like to make a change to scheduled remittances,
            please navigate to <b>Funds Deliveries </b> to submit your request.
          </Typography>

          <Typography paragraph align="left">
            {mistake}
          </Typography>
        </>
      )}
      {rejectReason === adminRejectedChangeRequest.UnjustifiedOrUnclear &&
        unjustifiedRequest()}
      {rejectReason === adminRejectedChangeRequest.Other && otherMessage()}
    </>
  );
}

function showRejectedPaymentMessage(
  rejectReason: number,
  incompleteData: () => Object,
  mistake: string,
  unjustifiedRequest: () => Object,
  otherMessage: () => Object,
) {
  const startingMessage: string =
    'Our team received your recent request to change one or more upcoming remittances.';

  return (
    <>
      <Typography paragraph align="left">
        {startingMessage}
      </Typography>{' '}
      {rejectReason === adminRejectedChangeRequest.IncompleteData &&
        incompleteData()}
      {rejectReason ===
        adminRejectedChangeRequest.MayLeadToDefaultUsersCashFlow && (
        <>
          <Typography paragraph align="left">
            We are unable to approve this request, as it may jeopardize your
            store’s cash flow. If you still need this change, we recommend you
            resubmit your request and select a nearer date to move the
            remittances to.
          </Typography>
          <Typography paragraph align="left">
            {mistake}
          </Typography>
        </>
      )}
      {rejectReason === adminRejectedChangeRequest.WrongVehicle && (
        <>
          <Typography paragraph align="left">
            We are unable to approve this request since it appears it should
            have been submitted via <b>Lines </b> instead.
          </Typography>
          <Typography paragraph align="left">
            Please ensure to only submit change requests via Funds Deliveries if
            you are experiencing or anticipating a cash flow surplus or shortage
            and would like to make a change to scheduled remittances.
          </Typography>
          <Typography paragraph align="left">
            You should submit a change request via Lines if a material change
            has occurred in your supply chain, such as a freight delay, the
            cancellation of a PO, or a change in the cost of a stage.
          </Typography>
          <Typography paragraph align="left">
            {mistake}
          </Typography>
        </>
      )}
      {rejectReason === adminRejectedChangeRequest.UnjustifiedOrUnclear &&
        unjustifiedRequest()}
      {rejectReason === adminRejectedChangeRequest.Other && otherMessage()}
    </>
  );
}

function showRejectedMessage(
  rejectReason: number,
  classes: any,
  firstName: Object,
  type: string,
  lineNames: Array<string> = [''],
) {
  const theme = process.env.REACT_APP_THEME;
  const madeMistake: string =
    'If you believe we made a mistake, please resubmit your request and include any supporting information that can help our team make a decision.';

  const incompleteData = (): Object => (
    <>
      <Typography paragraph align="left">
        We are unable to review this request at this time, as your bank account
        and/or your store appear to be disconnected from your{' '}
        {themeInfo[theme].company} account. Without these permissions,{' '}
        {themeInfo[theme].company} does not have sufficient data to thoroughly
        assess this request.
      </Typography>
      <Typography paragraph align="left">
        Account disconnection is a breach of our contract, and failure to
        reconnect the account may result in the interruption of your Active
        Growth Plan.
      </Typography>
      <Typography paragraph align="left">
        <b> What happens next?</b> <br />
        Verify that both your store and bank account are correctly connected,
        before attempting to resubmit your change requests.
      </Typography>
      <Typography paragraph align="left">
        If you believe we made a mistake, please resubmit your request, and
        include any supporting information that can help our team make a
        decision. This may include up to date bank statements for the last 3
        months.
      </Typography>
      <Typography paragraph align="left">
        To read more about the data we collect and how we make decisions, please
        see our{' '}
        <UrlLink href={faqUrl} target="_blank">
          FAQs
        </UrlLink>
        .
      </Typography>
    </>
  );

  const unjustifiedRequest = (): Object => (
    <>
      <Typography paragraph align="left">
        We are unable to approve this request, as the details provided are
        unclear or do not seem to support the request.
      </Typography>
      <Typography paragraph align="left">
        Please resubmit your request and include more details and supporting
        information that can help our team make a decision.
      </Typography>
    </>
  );

  const otherMessage = (): Object => (
    <>
      <Typography paragraph align="left" className={classes.message}>
        We are unable to approve this request.
      </Typography>
      <Typography paragraph align="left">
        {madeMistake}
      </Typography>
    </>
  );

  return (
    <>
      <Typography variant="h6" className={classes.message} gutterBottom>
        <b>Attn: Please amend + resubmit your application </b>
      </Typography>

      {type === 'financing_application' &&
        showRejectedLineMessage(
          rejectReason,
          incompleteData,
          madeMistake,
          unjustifiedRequest,
          otherMessage,
          lineNames,
          classes,
          firstName,
        )}
      {type === 'payment' &&
        showRejectedPaymentMessage(
          rejectReason,
          incompleteData,
          madeMistake,
          unjustifiedRequest,
          otherMessage,
        )}
    </>
  );
}

function RedirectToLine(reason, type) {
  if (
    reason === adminRejectedChangeRequest.UnsupportedGrowthAttempting ||
    reason === adminRejectedChangeRequest.FastExposureChanges ||
    reason === adminRejectedChangeRequest.LowMargin ||
    (reason === adminRejectedChangeRequest.UnjustifiedOrUnclear &&
      type === 'financing_application') ||
    (reason === adminRejectedChangeRequest.Other &&
      type === 'financing_application') ||
    (reason === adminRejectedChangeRequest.WrongVehicle && type === 'payment')
  )
    return true;

  return false;
}

function RedirectToPayment(reason, type) {
  if (
    reason === adminRejectedChangeRequest.MayLeadToDefaultUsersCashFlow ||
    (reason === adminRejectedChangeRequest.UnjustifiedOrUnclear &&
      type === 'payment') ||
    (reason === adminRejectedChangeRequest.Other && type === 'payment') ||
    (reason === adminRejectedChangeRequest.WrongVehicle &&
      type === 'financing_application')
  )
    return true;

  return false;
}

function ctaButtonText(redirectToLine, redirectToPayment, redirectToMyAccount) {
  if (redirectToLine) return 'Go To Lines';
  if (redirectToPayment) return 'Go To Funds Deliveries';
  if (redirectToMyAccount) return 'Go to My Account';
}

export default function Rejected({ request, showResponse, loading }) {
  const classes = useStyles();
  const history = useHistory();
  const theme = process.env.REACT_APP_THEME;

  const rejectReason = request?.admin_rejection_reason;
  const redirectToLine = RedirectToLine(rejectReason, request.type);
  const redirectToPayment = RedirectToPayment(rejectReason, request.type);
  const redirectToMyAccount =
    rejectReason === adminRejectedChangeRequest.IncompleteData;

  const ctaText = ctaButtonText(
    redirectToLine,
    redirectToPayment,
    redirectToMyAccount,
  );
  const onClickCompleteRequest = async () => {
    await showResponse(requestResponse.complete);
  };

  const onClickGoToRequest = () => {
    if (redirectToLine) {
      history.push(pathname.lines.url);
    } else if (redirectToPayment) {
      history.push(pathname.remittance.url);
    } else if (redirectToMyAccount) history.push(pathname.account.main.url);
  };

  const firstName = (
    <Typography paragraph align="left" className={classes.message}>
      {startCase(request.first_name)},
    </Typography>
  );

  return (
    <>
      <DialogContent className={classes.root}>
        {rejectReason !== null && (
          <>
            <MailOutlineIcon className={classes.mailLogo} />
            {rejectReason &&
              showRejectedMessage(
                rejectReason,
                classes,
                firstName,
                request.type,
                request.lineNames,
              )}
            <AdminFreeText freeText={request?.admin_free_text} />
          </>
        )}
        {!request?.admin_rejection_reason && (
          <>
            <img
              src={'./icons/edit.svg'}
              className={classes.logo}
              alt="edit logo"
            />
            <Typography variant="h5" className={classes.topMargin}>
              Your requested change <br /> was not applied.
            </Typography>
            <Typography variant="h6">
              In some cases, we may reverse this decision.
            </Typography>
            <Typography variant="h6">
              If you would like to provide more information <br /> to support
              your request, please e-mail <br />
              <a href={`mailto:${themeInfo[theme].email}`}>
                {' '}
                {themeInfo[theme].email}
              </a>{' '}
              as soon as possible.
            </Typography>
          </>
        )}

        <DialogActions className={classes.dialogAction}>
          <div>
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              onClick={onClickGoToRequest}
              disabled={loading}
            >
              <b>{ctaText}</b>
            </Button>
          </div>
          <div>
            <LoadingButton
              className={classes.button}
              color="default"
              onClick={onClickCompleteRequest}
              loading={loading}
            >
              <b>Don’t show this message again</b>
            </LoadingButton>
          </div>
        </DialogActions>
      </DialogContent>
    </>
  );
}
